import { PersonAffiliation, PersonRole } from '../../../types'
import { latestAffiliationActivityDate } from '../../../utils/personRecentActivity'
import {
  formatDate,
  formatPersonRoleLabel,
  isDissolvedStatus,
} from '../../../utils/format'

/** One company, all its roles for this person collapsed onto a single row. */
export interface CompanyRoleEntry {
  affiliation: PersonAffiliation
  roles: PersonRole[]
}

export type AffiliationViewMode = 'list' | 'cards'

export type AffiliationSortKey =
  | 'name'
  | 'active_since_desc'
  | 'active_since_asc'
  | 'recent_change_desc'

export type AffiliationListVariant = 'active' | 'historic'

export const AFFILIATION_VIEW_STORAGE_KEY = 'person-affiliations-view'

function sortStorageKey(variant: AffiliationListVariant): string {
  return variant === 'active'
    ? 'person-affiliations-sort'
    : 'person-affiliations-sort-historic'
}

export const AFFILIATION_SORT_OPTIONS_ACTIVE: ReadonlyArray<{
  value: AffiliationSortKey
  label: string
}> = [
  { value: 'name', label: 'Alfabetisk' },
  { value: 'active_since_desc', label: 'Aktiv siden (nyest)' },
  { value: 'active_since_asc', label: 'Aktiv siden (ældst)' },
  { value: 'recent_change_desc', label: 'Seneste ændring' },
]

export const AFFILIATION_SORT_OPTIONS_HISTORIC: ReadonlyArray<{
  value: AffiliationSortKey
  label: string
}> = [
  { value: 'name', label: 'Alfabetisk' },
  { value: 'active_since_desc', label: 'Startet (nyest)' },
  { value: 'active_since_asc', label: 'Startet (ældst)' },
  { value: 'recent_change_desc', label: 'Seneste ændring' },
]

/** @deprecated use AFFILIATION_SORT_OPTIONS_ACTIVE */
export const AFFILIATION_SORT_OPTIONS = AFFILIATION_SORT_OPTIONS_ACTIVE

export function getAffiliationSortOptions(
  variant: AffiliationListVariant,
): ReadonlyArray<{ value: AffiliationSortKey; label: string }> {
  return variant === 'active'
    ? AFFILIATION_SORT_OPTIONS_ACTIVE
    : AFFILIATION_SORT_OPTIONS_HISTORIC
}

/** One entry per company, collecting all matching roles. Sorted by company name. */
export function groupByCompany(
  affiliations: PersonAffiliation[],
  active: boolean,
): CompanyRoleEntry[] {
  const map = new Map<string, CompanyRoleEntry>()
  for (const aff of affiliations) {
    const dissolved = isDissolvedStatus(aff.company.status)
    let matchingRoles: PersonRole[]
    if (active) {
      if (dissolved) continue
      matchingRoles = aff.roles.filter((r) => r.isActive)
    } else if (dissolved) {
      matchingRoles = aff.roles
    } else {
      matchingRoles = aff.roles.filter((r) => !r.isActive)
    }
    if (!matchingRoles.length) continue
    const key = aff.company.cvrNummer || aff.company.navn || String(Math.random())
    if (!map.has(key)) {
      map.set(key, { affiliation: aff, roles: [] })
    }
    map.get(key)!.roles.push(...matchingRoles)
  }
  return Array.from(map.values()).sort((a, b) =>
    (a.affiliation.company.navn ?? '').localeCompare(b.affiliation.company.navn ?? '', 'da'),
  )
}

export function formatActiveSince(from: string | null): string | null {
  if (!from) return null
  return formatDate(from.split('T')[0])
}

export function formatAffiliationPeriod(from: string | null, to: string | null): string {
  const f = from ? formatDate(from.split('T')[0]) : null
  const t = to ? formatDate(to.split('T')[0]) : null
  if (f && t) return `${f} – ${t}`
  if (f && !t) return `${f} –`
  if (!f && t) return `– ${t}`
  return 'Ukendt periode'
}

export function roleLabel(role: PersonRole): string {
  return formatPersonRoleLabel(role.function || role.organizationType)
}

export function entryRoleLabels(entry: CompanyRoleEntry): string[] {
  return entry.roles.map(roleLabel)
}

/** Unique role labels across entries, sorted for stable chip order. */
export function collectUniqueRoleLabels(entries: CompanyRoleEntry[]): string[] {
  const labels = new Set<string>()
  for (const entry of entries) {
    for (const label of entryRoleLabels(entry)) {
      labels.add(label)
    }
  }
  return Array.from(labels).sort((a, b) => a.localeCompare(b, 'da'))
}

export function affiliationPeriod(entry: CompanyRoleEntry): {
  from: string | null
  to: string | null
} {
  const { roles } = entry
  const froms = roles.map((r) => r.validFrom).filter(Boolean) as string[]
  const tos = roles.map((r) => r.validTo).filter(Boolean) as string[]
  const from = froms.length ? froms.reduce((a, b) => (a < b ? a : b)) : null
  const to =
    tos.length === roles.length ? tos.reduce((a, b) => (a > b ? a : b)) : null
  return { from, to }
}

function normaliseQuery(raw: string): string {
  return raw.trim().toLowerCase()
}

export function entryMatchesText(entry: CompanyRoleEntry, query: string): boolean {
  const q = normaliseQuery(query)
  if (!q) return true
  const { company } = entry.affiliation
  const haystack = [company.navn, company.cvrNummer, company.virksomhedsform]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(q)
}

export function entryMatchesRoles(
  entry: CompanyRoleEntry,
  selectedRoles: ReadonlySet<string>,
): boolean {
  if (selectedRoles.size === 0) return true
  return entryRoleLabels(entry).some((label) => selectedRoles.has(label))
}

export function filterAffiliationEntries(
  entries: CompanyRoleEntry[],
  textQuery: string,
  selectedRoles: ReadonlySet<string>,
): CompanyRoleEntry[] {
  return entries.filter(
    (entry) => entryMatchesText(entry, textQuery) && entryMatchesRoles(entry, selectedRoles),
  )
}

function isoSortKey(iso: string | null | undefined): string {
  return iso?.split('T')[0] ?? ''
}

export function entryActiveSince(entry: CompanyRoleEntry): string | null {
  return affiliationPeriod(entry).from
}

export function entryLatestChange(entry: CompanyRoleEntry): string | null {
  return latestAffiliationActivityDate(entry.affiliation)
}

export function sortAffiliationEntries(
  entries: CompanyRoleEntry[],
  sortKey: AffiliationSortKey,
): CompanyRoleEntry[] {
  const sorted = [...entries]
  sorted.sort((a, b) => {
    switch (sortKey) {
      case 'name':
        return (a.affiliation.company.navn ?? '').localeCompare(
          b.affiliation.company.navn ?? '',
          'da',
        )
      case 'active_since_desc': {
        const aKey = isoSortKey(entryActiveSince(a))
        const bKey = isoSortKey(entryActiveSince(b))
        if (aKey === bKey) return compareByName(a, b)
        if (!aKey) return 1
        if (!bKey) return -1
        return bKey.localeCompare(aKey)
      }
      case 'active_since_asc': {
        const aKey = isoSortKey(entryActiveSince(a))
        const bKey = isoSortKey(entryActiveSince(b))
        if (aKey === bKey) return compareByName(a, b)
        if (!aKey) return 1
        if (!bKey) return -1
        return aKey.localeCompare(bKey)
      }
      case 'recent_change_desc': {
        const aKey = isoSortKey(entryLatestChange(a))
        const bKey = isoSortKey(entryLatestChange(b))
        if (aKey === bKey) return compareByName(a, b)
        if (!aKey) return 1
        if (!bKey) return -1
        return bKey.localeCompare(aKey)
      }
      default:
        return 0
    }
  })
  return sorted
}

function compareByName(a: CompanyRoleEntry, b: CompanyRoleEntry): number {
  return (a.affiliation.company.navn ?? '').localeCompare(
    b.affiliation.company.navn ?? '',
    'da',
  )
}

export function readStoredAffiliationView(): AffiliationViewMode {
  if (typeof window === 'undefined') return 'list'
  try {
    const raw = window.localStorage.getItem(AFFILIATION_VIEW_STORAGE_KEY)
    return raw === 'cards' ? 'cards' : 'list'
  } catch {
    return 'list'
  }
}

export function readStoredAffiliationSort(
  variant: AffiliationListVariant = 'active',
): AffiliationSortKey {
  const fallback: AffiliationSortKey =
    variant === 'historic' ? 'recent_change_desc' : 'name'
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(sortStorageKey(variant))
    const options = getAffiliationSortOptions(variant)
    return options.some((o) => o.value === raw) ? (raw as AffiliationSortKey) : fallback
  } catch {
    return fallback
  }
}

export function storeAffiliationView(mode: AffiliationViewMode): void {
  try {
    window.localStorage.setItem(AFFILIATION_VIEW_STORAGE_KEY, mode)
  } catch {
    // ignore quota / private mode
  }
}

export function storeAffiliationSort(
  sortKey: AffiliationSortKey,
  variant: AffiliationListVariant = 'active',
): void {
  try {
    window.localStorage.setItem(sortStorageKey(variant), sortKey)
  } catch {
    // ignore quota / private mode
  }
}
